import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";

const OutletCtx = createContext(null);

export function OutletProvider({ children }) {
  const { user } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [meta, setMeta] = useState({ max_allowed: 1, included: 1, is_multi_outlet_plan: false, remaining_slots: 0, is_unlimited: false });
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem("studioops_active_outlet") || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user || user === false) { setOutlets([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/outlets");
      setOutlets(data.items || []);
      setMeta({
        max_allowed: data.max_allowed,
        included: data.included,
        is_multi_outlet_plan: data.is_multi_outlet_plan,
        additional_monthly_eur: data.additional_monthly_eur,
        remaining_slots: data.remaining_slots ?? 0,
        is_unlimited: !!data.is_unlimited,
      });
      const ids = (data.items || []).map((o) => o.id);
      if (!activeId || !ids.includes(activeId)) {
        const fallback = (data.items || []).find((o) => o.is_default) || (data.items || [])[0];
        if (fallback) {
          setActiveId(fallback.id);
          try { localStorage.setItem("studioops_active_outlet", fallback.id); } catch { /* ignore */ }
        }
      }
    } catch {
      setOutlets([]);
    } finally {
      setLoading(false);
    }
  }, [user, activeId]);

  useEffect(() => { reload(); }, [reload]);

  const switchOutlet = (id) => {
    setActiveId(id);
    try { localStorage.setItem("studioops_active_outlet", id); } catch { /* ignore */ }
  };

  const activeOutlet = outlets.find((o) => o.id === activeId) || outlets[0] || null;

  return (
    <OutletCtx.Provider value={{ outlets, meta, activeId, activeOutlet, switchOutlet, reload, loading }}>
      {children}
    </OutletCtx.Provider>
  );
}

export const useOutlets = () => {
  const ctx = useContext(OutletCtx);
  if (!ctx) return { outlets: [], meta: {}, activeId: null, activeOutlet: null, switchOutlet: () => {}, reload: () => {}, loading: false };
  return ctx;
};
